import settings from 'popups/Settings'
import { classInfo } from '../CalendarState'
import { _removeInstances } from '../EventInstanceManager'
import type { AppCalendar, CalendarEventObject } from '../CalendarSync'
import type { ICSSubscription, RefreshInterval } from './types'
import { REFRESH_INTERVALS } from './types'

const VALID_INTERVALS = new Set<string>(Object.keys(REFRESH_INTERVALS))

/**
 * 유효하지 않은 refreshInterval(예: 제거된 'every_month')을 가장 가까운 유효 값으로 변환한다.
 */
export const sanitizeRefreshInterval = (value: string): RefreshInterval =>
  VALID_INTERVALS.has(value) ? (value as RefreshInterval) : 'every_week'

/**
 * settings에서 구독 목록을 읽어온다.
 * 유효하지 않은 refreshInterval은 자동으로 변환한다.
 */
export const _loadSubscriptions = (): ICSSubscription[] => {
  const calendarSettings = settings.getValue('calendar') || {}
  const subs: ICSSubscription[] = calendarSettings.icsSubscriptions || []
  for (const sub of subs) {
    sub.refreshInterval = sanitizeRefreshInterval(sub.refreshInterval)
  }
  return subs
}

/**
 * settings에 구독 목록을 저장한다.
 */
export const _saveSubscriptions = (subscriptions: ICSSubscription[]): Promise<void> => {
  const calendarSettings = settings.getValue('calendar') || {}
  return settings.setValue('calendar', {
    ...calendarSettings,
    icsSubscriptions: subscriptions,
  })
}

/**
 * 구독 ID로 synthetic calendar URL을 생성한다.
 */
export const _makeCalendarUrl = (subscriptionId: string): string => {
  return `subscription://${subscriptionId}/`
}

/**
 * 구독 ID로 이벤트 synthetic URL을 생성한다.
 */
export const _makeEventUrl = (subscriptionId: string, eventUid: string): string => {
  return `subscription://${subscriptionId}/${eventUid}`
}

/**
 * classInfo.calendars에서 subscription AppCalendar를 찾는다.
 */
export const _findCalendar = (subscriptionId: string): AppCalendar | null => {
  const calendarUrl = _makeCalendarUrl(subscriptionId)
  const found = classInfo.calendars?.find((c) => c.url === calendarUrl)
  return (found as AppCalendar) || null
}

/**
 * ICSSubscription으로부터 AppCalendar 객체를 생성한다.
 */
export const _createAppCalendar = (
  sub: ICSSubscription,
  existingEvents?: Record<string, CalendarEventObject>,
): AppCalendar => {
  return {
    url: _makeCalendarUrl(sub.id),
    displayName: sub.displayName,
    calendarColor: { _cdata: sub.color },
    folderType: 'subscription',
    isDefault: false,
    isSync: sub.isSync,
    isShared: false,
    isSharedOwner: false,
    isReadOnly: true,
    events: existingEvents || {},
  }
}

/**
 * classInfo.calendars에 구독 캘린더를 추가하거나 업데이트한다.
 */
export const _upsertCalendar = (calendar: AppCalendar): void => {
  if (!classInfo.calendars) {
    classInfo.calendars = []
  }

  const index = classInfo.calendars.findIndex((c) => c.url === calendar.url)
  if (index >= 0) {
    classInfo.calendars[index] = calendar
  } else {
    classInfo.calendars.push(calendar)
  }
}

/**
 * classInfo.calendars에서 구독 캘린더를 제거한다.
 */
export const _removeCalendar = (subscriptionId: string): void => {
  const calendarUrl = _makeCalendarUrl(subscriptionId)
  if (!classInfo.calendars) return

  const calendar = classInfo.calendars.find((c) => c.url === calendarUrl) as AppCalendar | undefined
  if (calendar) {
    _removeInstances(calendar.events)
    classInfo.calendars = classInfo.calendars.filter((c) => c.url !== calendarUrl)
  }
}
